import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { fetchAccessories } from "../api/accessories";
import { fetchSpares } from "../api/spares";
import ArrowLeft from "../assets/icons/arrow-left.svg";
import NavBar from "../components/navigation";
import Footer from "../components/Footer";

const SPARE_CATEGORIES = {
  "service-parts": { title: "Service Parts", categories: ["filters", "fluids", "belts", "cooling", "fuel", "exhaust"] },
  "tyres-and-rims": { title: "Tyres and Rims", categories: ["brakes", "wheels"] },
  "suspension-and-steering": { title: "Suspension and Steering", categories: ["suspension", "steering"] },
  "vehicle-batteries": { title: "Vehicle Batteries", categories: ["electrical", "batteries"] },
  brakes: { title: "Brakes", category: "brakes" },
  "electrical-and-lighting": { title: "Electrical and Lighting", categories: ["electrical"] },
  "engine-gearbox-and-transmission": { title: "Engine, Gearbox and Transmission", categories: ["engine", "transmission"] },
  "body-parts": { title: "Body Parts", category: "body" },
  "vehicle-accessories": { title: "Vehicle Accessories", categories: ["interior", "other"] },
};

const ACCESSORY_CATEGORIES = {
  projectors: { title: "Projector Headlights", category: "projectors" },
  "led-headlights": { title: "LED Headlights", category: "projectors" },
  speakers: { title: "Speakers", category: "speakers" },
  infotainment: { title: "Infotainment Systems", category: "infotainment" },
  "gps-trackers": { title: "GPS Trackers", category: "gps_trackers" },
  "dash-cameras": { title: "Dash Cameras", category: "cameras" },
  "reverse-cameras": { title: "Reverse Cameras", category: "cameras" },
  "body-parts": { title: "Body Parts and Exterior Accessories", category: "body" },
  "tires-wheels": { title: "Tires and Wheels", category: "wheels" },
  "lights-electrical": { title: "Lights, Electrical and Sensors", category: "projectors" },
};

const formatPrice = (value, currency = "KES") => {
  const symbol = currency === "USD" ? "$" : "Ksh ";
  return `${symbol}${Number(value || 0).toLocaleString("en-US")}`;
};

export function ListingCard({ item, type }) {
  const title = type === "spare" ? item.name : item.title;
  const image = item.images?.[0] || item.image;

  return (
    <article className="overflow-hidden rounded-xl border border-border bg-background shadow-sm">
      <div className="flex h-48 items-center justify-center bg-gray-100">
        {image ? (
          <img src={image} alt={title} className="h-full w-full object-cover" />
        ) : (
          <span className="text-sm text-muted">No image available</span>
        )}
      </div>
      <div className="p-5">
        <p className="text-xs font-semibold uppercase tracking-wide text-primary">{item.category}</p>
        <h2 className="mt-2 text-xl font-heading font-bold text-dark">{title || "Unnamed part"}</h2>
        {item.description && <p className="mt-2 line-clamp-2 text-sm text-muted">{item.description}</p>}
        <div className="mt-5 flex items-center justify-between border-t border-border pt-4">
          <strong className="text-lg text-primary">{formatPrice(item.price, item.currency)}</strong>
          <Link to={`/${type === "spare" ? "parts" : "accessories"}/item/${item.id}`} className="text-sm font-bold text-primary hover:underline">
            View details
          </Link>
        </div>
      </div>
    </article>
  );
}

export default function CategoryPage({ type }) {
  const { category: slug } = useParams();
  const isAccessory = type === "accessory";
  const definition = (isAccessory ? ACCESSORY_CATEGORIES : SPARE_CATEGORIES)[slug] ||
    (isAccessory && !slug ? { title: "All Accessories" } : null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(Boolean(definition));
  const [error, setError] = useState("");

  useEffect(() => {
    if (!definition) {
      setLoading(false);
      return;
    }

    let active = true;
    const loadItems = async () => {
      try {
        const results = isAccessory
          ? await fetchAccessories(definition.category ? { category: definition.category } : {})
          : await fetchSpares({ category: definition.categories || [definition.category] });
        if (active) setItems(Array.isArray(results) ? results : []);
      } catch (requestError) {
        if (active) setError(requestError.message || "Unable to load listings.");
      } finally {
        if (active) setLoading(false);
      }
    };

    loadItems();
    return () => {
      active = false;
    };
  }, [definition, isAccessory]);

  if (!definition) {
    return (
      <main className="mx-auto max-w-3xl px-6 py-32 text-center">
        <h1 className="text-4xl font-heading font-bold text-dark">Category not found</h1>
        <p className="mt-3 text-muted">That category is no longer available.</p>
        <Link to={isAccessory ? "/accessories" : "/parts"} className="mt-8 inline-block font-bold text-primary hover:underline">
          Browse all categories
        </Link>
      </main>
    );
  }

  return (
    <>
    <NavBar />
    <main className="min-h-screen bg-background px-6 pb-20 pt-32 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <Link to={isAccessory ? "/accessories" : "/parts"} className="text-sm font-bold text-primary hover:underline flex items-center gap-1">
          <img src={ArrowLeft} alt="" className="inline-block h-4 w-4" /> All {isAccessory ? "accessories" : "parts"}
        </Link>
        <div className="mt-6 border-b border-border pb-8">
          <p className="text-sm font-semibold uppercase tracking-widest text-primary">My Car marketplace</p>
          <h1 className="mt-2 text-4xl font-heading font-bold text-dark">{definition.title}</h1>
          <p className="mt-3 text-muted">Find quality {isAccessory ? "accessories" : "replacement parts"} from trusted sellers.</p>
        </div>

        {loading && <p className="py-20 text-center text-muted">Loading listings...</p>}
        {!loading && error && <p className="py-20 text-center text-red-500">Could not load listings: {error}</p>}
        {!loading && !error && items.length === 0 && (
          <div className="py-20 text-center">
            <h2 className="text-2xl font-heading font-bold text-dark">No listings yet</h2>
            <p className="mt-2 text-muted">Check back soon or contact us to help source this category.</p>
            <Link to="/contact" className="mt-6 inline-block rounded-md bg-primary px-6 py-3 font-bold text-white">
              Contact us
            </Link>
          </div>
        )}
        {!loading && !error && items.length > 0 && (
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((item) => <ListingCard key={item.id} item={item} type={type} />)}
          </div>
        )}
      </div>
    </main>
        <Footer />
    </>
  );
}